/*
 * <asa-dropdown>: behaviour for snippets/dropdown.liquid. The trigger click
 * toggles the menu; outside click and Escape close it; ArrowDown/ArrowUp move
 * between options. In select mode a choice is written into the native
 * <select> (which stays in the form and fires `change`) and mirrored into
 * the value span, which is also part of the trigger's accessible name. The
 * native select is hidden from focus and assistive tech, so a click on the
 * field label focuses the trigger instead. Submit-mode options are plain
 * submit buttons and need nothing.
 */
if (!customElements.get('asa-dropdown')) {
  customElements.define(
    'asa-dropdown',
    class extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[data-dropdown-toggle]');
        this.select = this.querySelector('[data-dropdown-select]');
        this.current = this.querySelector('[data-dropdown-current]');
        if (!this.trigger) return;
        this.setAttribute('data-js', '');
        if (this.select) {
          this.select.tabIndex = -1;
          this.select.setAttribute('aria-hidden', 'true');
        }
        this.querySelector('[data-dropdown-label]')?.addEventListener('click', (event) => {
          event.preventDefault(); // its `for` would focus the hidden select
          this.trigger.focus();
        });
        this.trigger.addEventListener('click', () => {
          // Safari does not focus a clicked button; without focus inside, Escape would reach the surrounding dialog instead.
          this.trigger.focus({ preventScroll: true });
          this.toggle(!this.classList.contains('is-open'));
        });
        this.addEventListener('click', this.onOptionClick);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      options() {
        return Array.from(this.querySelectorAll('[data-dropdown-option]'));
      }

      toggle(open) {
        this.classList.toggle('is-open', open);
        this.trigger.setAttribute('aria-expanded', String(open));
      }

      onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.toggle(false);
      };

      onOptionClick = (event) => {
        const option = event.target.closest('[data-dropdown-value]');
        if (!option || !this.select) return;
        this.select.value = option.dataset.dropdownValue;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.options().forEach((item) => {
          if (item === option) item.setAttribute('aria-current', 'true');
          else item.removeAttribute('aria-current');
        });
        this.current.textContent = option.textContent.trim();
        this.current.classList.remove('dropdown__current--empty');
        this.toggle(false);
        this.trigger.focus();
      };

      onKeydown = (event) => {
        if (event.key === 'Escape') {
          if (!this.classList.contains('is-open')) return;
          event.preventDefault(); // no dialog cancel: Escape closes the menu, not a drawer around it
          this.toggle(false);
          this.trigger.focus();
          return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const items = this.options();
        if (!items.length) return;
        event.preventDefault();
        if (!this.classList.contains('is-open')) this.toggle(true);
        const index = items.indexOf(document.activeElement);
        const step = event.key === 'ArrowDown' ? 1 : -1;
        items[(index + step + items.length) % items.length].focus();
      };
    }
  );
}
